import {
  Button,
  Css,
  GridColumn,
  GridTable,
  IconButton,
  MultiSelectField,
  SelectField,
  actionColumn,
  column,
  simpleHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useMemo, useRef } from "react";
import { createProductOfferingUrl } from "src/RouteUrls";
import { ProductOfferingStartingConfigType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  ProductOfferingUwStartingConfig_OptionFragment,
  ProductOfferingUwStartingConfigsPageQuery,
  useProductOfferingUwStartingConfigsPageQuery,
  useProductOfferingUwStartingConfigs_CheckoutOptionsQuery,
  useProductOfferingUwStartingConfigs_DeleteMutation,
  useProductOfferingUwStartingConfigs_SaveMutation,
} from "src/routes/developments/product-offerings/ProductOfferingUwStartingConfigsPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { ProductOfferingVersionParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";

export function ProductOfferingUwStartingConfigsPage() {
  const { productOfferingId } = useRouteParams<ProductOfferingVersionParams>();
  const query = useProductOfferingUwStartingConfigsPageQuery({
    variables: { productOfferingId },
    fetchPolicy: "cache-and-network",
  });

  return queryResult(query, { data: (data) => <ProductOfferingUwStartingConfigsPageView data={data} /> });
}

type ProductOfferingUwStartingConfigsPageViewProps = { data: ProductOfferingUwStartingConfigsPageQuery };

function ProductOfferingUwStartingConfigsPageView(props: ProductOfferingUwStartingConfigsPageViewProps) {
  const { data } = props;
  const { productOffering, productOfferingStartingConfigs } = data;
  const { rpavId } = useRouteParams<ProductOfferingVersionParams>();
  const [saveStartingConfig] = useProductOfferingUwStartingConfigs_SaveMutation();
  const [deleteStartingConfig] = useProductOfferingUwStartingConfigs_DeleteMutation();
  const { openModal } = useModal();

  const optionsQuery = useProductOfferingUwStartingConfigs_CheckoutOptionsQuery({
    variables: { productOfferingId: productOffering.id, versionId: productOffering.aggregateCheckout?.id },
    skip: !productOffering.aggregateCheckout,
  });
  const { designGroups, otherOptions } = useMemo(
    () => splitByDesignPackageGroup(optionsQuery.data?.productOffering.options ?? []),
    [optionsQuery.data],
  );

  const formState = useFormState({
    config: formConfig,
    // Rows are saved and deleted individually, so the form owns row state after the first map;
    // re-mapping on every cache change would wipe other rows' in-flight edits and unsaved drafts.
    init: { input: productOfferingStartingConfigs.entities, map: mapToForm, onlyOnce: true },
  });

  // New rows have no server id, so they carry a client id to keep a stable GridDataRow identity.
  const nextClientId = useRef(0);
  function addRow() {
    formState.startingConfigs.add({ clientId: `new:${nextClientId.current++}`, pinnedOptionIds: [] });
  }

  async function saveRow(row: ObjectState<StartingConfigRow>) {
    const { data: saved } = await saveStartingConfig({ variables: { input: mapRowToInput(row, productOffering.id) } });
    // A new row comes back with its server id, which turns it into a persisted row.
    const savedId = saved?.saveProductOfferingStartingConfig.productOfferingStartingConfig.id;
    if (savedId) row.id.set(savedId);
    row.commitChanges();
  }

  /** Saved configs are gone for good, so confirm first; unsaved drafts just disappear. */
  function confirmDeleteRow(row: ObjectState<StartingConfigRow>) {
    if (!row.id.value) {
      formState.startingConfigs.remove(row.value);
      return;
    }
    openModal({
      content: (
        <ConfirmationModal
          title="Delete Starting Config"
          // Distinct from the row's trash button so the two do not share a derived test id
          label="Delete Config"
          danger
          confirmationMessage={
            <>
              Are you sure you want to delete underwriting starting config <span css={Css.smSb.$}>{row.id.value}</span>?
              This cannot be undone.
            </>
          }
          onConfirmAction={() => deleteRow(row)}
        />
      ),
    });
  }

  async function deleteRow(row: ObjectState<StartingConfigRow>) {
    const id = row.id.value;
    if (id) {
      await deleteStartingConfig({
        variables: { input: { id } },
        // Drop it from the cache so a later visit doesn't resurrect it; the form list is our source
        // of truth for what is on screen, so no refetch is needed.
        update: (cache) => cache.evict({ id: cache.identify({ __typename: "ProductOfferingStartingConfig", id }) }),
      });
    }
    formState.startingConfigs.remove(row.value);
  }

  const columns = useMemo(
    () => createColumns(designGroups, otherOptions, saveRow, confirmDeleteRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [designGroups, otherOptions],
  );
  const rows = useComputed(
    () => [
      simpleHeader,
      ...formState.startingConfigs.rows.map(
        (row) => ({ kind: "config" as const, id: rowId(row), data: row }) satisfies StartingConfigGridRow,
      ),
    ],
    [formState],
  );

  return (
    <>
      <PageHeader
        title={`Edit UW Starting Configs for ${productOffering.name}`}
        breadcrumb={{
          label: productOffering.name,
          // UW starting configs are only used by project-based offerings, so there is no
          // development to route back through.
          href: createProductOfferingUrl(productOffering.id, rpavId, undefined, productOffering.project?.id),
        }}
        right={
          <Button
            label="Add Config"
            variant="secondary"
            onClick={addRow}
            disabled={!productOffering.aggregateCheckout && "Product Offering has no promoted checkout version"}
          />
        }
      />
      <GridTable
        columns={columns}
        rows={rows}
        fallbackMessage="No underwriting starting configs yet. Use Add Config to create one."
      />
    </>
  );
}

type StartingConfigGridRow = { kind: "config"; id: string; data: ObjectState<StartingConfigRow> } | { kind: "header" };

/** An option group that gets its own column, i.e. one design choice per starting config. */
type DesignGroup = {
  id: string;
  name: string;
  order: number;
  options: ProductOfferingUwStartingConfig_OptionFragment[];
};

function createColumns(
  designGroups: DesignGroup[],
  otherOptions: ProductOfferingUwStartingConfig_OptionFragment[],
  onSave: (row: ObjectState<StartingConfigRow>) => Promise<void>,
  onDelete: (row: ObjectState<StartingConfigRow>) => void,
): GridColumn<StartingConfigGridRow>[] {
  const otherOptionIds = new Set(otherOptions.map((option) => option.id));
  return [
    column<StartingConfigGridRow>({
      header: "Id",
      config: (row) => <span css={Css.xs.gray700.$}>{row.id.value ?? "New"}</span>,
      w: "120px",
    }),
    // One column per design-package group, so the columns follow the offering's own configuration.
    ...designGroups.map((group) => designGroupColumn(group)),
    column<StartingConfigGridRow>({
      header: "Other Options",
      config: (row) => {
        const pinned = row.pinnedOptionIds.value ?? [];
        return (
          <MultiSelectField
            data-testid="otherOptions"
            label="Other Options"
            labelStyle="hidden"
            values={pinned.filter((id) => otherOptionIds.has(id))}
            options={toSelectOptions(otherOptions)}
            onSelect={(ids) => row.pinnedOptionIds.set([...pinned.filter((id) => !otherOptionIds.has(id)), ...ids])}
            nothingSelectedText="None"
          />
        );
      },
      w: 2,
    }),
    actionColumn<StartingConfigGridRow>({
      header: "",
      config: (row) => (
        <div css={Css.df.gap1.aic.$}>
          <Button label="Save" disabled={saveRowDisabledReason(row)} onClick={() => onSave(row)} />
          <IconButton data-testid="delete" icon="trash" label="Delete" onClick={() => onDelete(row)} />
        </div>
      ),
      w: "150px",
    }),
  ];
}

function designGroupColumn(group: DesignGroup): GridColumn<StartingConfigGridRow> {
  const groupOptionIds = new Set(group.options.map((option) => option.id));
  return column<StartingConfigGridRow>({
    header: group.name,
    config: (row) => {
      const pinned = row.pinnedOptionIds.value ?? [];
      return (
        <SelectField
          data-testid="designOption"
          label={group.name}
          labelStyle="hidden"
          value={pinned.find((id) => groupOptionIds.has(id))}
          options={toSelectOptions(group.options)}
          // A starting config pins at most one option per design group, so selecting replaces.
          onSelect={(optionId) =>
            row.pinnedOptionIds.set([...pinned.filter((id) => !groupOptionIds.has(id)), ...[optionId].compact()])
          }
          nothingSelectedText="—"
        />
      );
    },
    w: 1.5,
  });
}

/** Save is only enabled once this line itself has been edited. */
function saveRowDisabledReason(row: ObjectState<StartingConfigRow>) {
  if (!row.dirty) return "No changes on this line";
  if (!row.valid) return "Resolve this line's errors before saving";
  return false;
}

/** Beam's `HasIdAndName` overload wants `name`, but RPOs read better by `displayName`. */
function toSelectOptions(options: ProductOfferingUwStartingConfig_OptionFragment[]) {
  return options.map((option) => ({ id: option.id, name: option.displayName }));
}

/** Design-package groups each get a column; everything else falls into "Other Options". */
function splitByDesignPackageGroup(options: readonly ProductOfferingUwStartingConfig_OptionFragment[]) {
  const active = options.filter((option) => option.active && option.optionGroup.active);
  const groupsById = new Map<string, DesignGroup>();
  const otherOptions: ProductOfferingUwStartingConfig_OptionFragment[] = [];
  for (const option of active) {
    const { id, name, order, forDesignPackages } = option.optionGroup;
    if (!forDesignPackages) {
      otherOptions.push(option);
      continue;
    }
    const group = groupsById.get(id) ?? { id, name, order, options: [] };
    group.options.push(option);
    groupsById.set(id, group);
  }
  return {
    designGroups: [...groupsById.values()]
      .sortBy((group) => group.order)
      .map((group) => ({ ...group, options: group.options.sortBy((o) => o.order ?? Number.POSITIVE_INFINITY) })),
    otherOptions: otherOptions.sortBy((o) => o.order ?? Number.POSITIVE_INFINITY),
  };
}

function rowId(row: ObjectState<StartingConfigRow>): string {
  return row.id.value ?? row.clientId.value ?? "new";
}

type StartingConfigRow = {
  id?: string | null;
  /** FE-only stable identity for rows that have not been saved yet. */
  clientId?: string | null;
  /** The flat set of pinned options, exactly as the mutation takes them. */
  pinnedOptionIds?: string[] | null;
};

type StartingConfigsForm = { startingConfigs: StartingConfigRow[] };

const formConfig: ObjectConfig<StartingConfigsForm> = {
  startingConfigs: {
    type: "list",
    config: { id: { type: "value" }, clientId: { type: "value" }, pinnedOptionIds: { type: "value" } },
  },
};

function mapToForm(
  entities: ProductOfferingUwStartingConfigsPageQuery["productOfferingStartingConfigs"]["entities"],
): StartingConfigsForm {
  return {
    startingConfigs: entities.map((config) => ({
      id: config.id,
      pinnedOptionIds: config.pinnedOptions.map((option) => option.id),
    })),
  };
}

function mapRowToInput(row: ObjectState<StartingConfigRow>, productOfferingId: string) {
  const { id, pinnedOptionIds } = row.value;
  return {
    id,
    productOfferingId,
    type: ProductOfferingStartingConfigType.Underwriting,
    pinnedOptionIds: pinnedOptionIds ?? [],
  };
}
